import { create } from "zustand";
import { supabase } from "@/utils/supabase";

// SQL 함수에서 반환하는 사용자 데이터 타입
interface AuthUser {
  id: string;
  username: string;
  user_color: string;
  created_at?: string;
}

interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  initialized: boolean;
  hasCompletedIntro: boolean; // 인트로 완료 여부

  // Actions
  setUser: (user: AuthUser | null) => void;
  setLoading: (loading: boolean) => void;
  setIntroCompleted: (completed: boolean) => void;
  initialize: () => Promise<void>;
  signIn: (
    username: string,
    password: string
  ) => Promise<{ error: Error | null }>;
  signUp: (
    username: string,
    password: string
  ) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  getUsername: () => string | null;
  getUserId: () => string | null;
  anonymousSignIn: () => Promise<{ error: Error | null }>;
  isAnonymous: () => boolean;
  reset: () => void; // 상태 초기화
  updateUserColor: (color: string) => Promise<{ error: Error | null }>;
}

/**
 * 인증 상태 관리 스토어
 * Supabase SQL 함수(RPC)를 통한 인증 관리
 * 세션 유지 비활성화 - 매번 접속 시 인트로에서 시작
 */
export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: false,
  initialized: true, // 기본적으로 초기화된 상태로 시작
  hasCompletedIntro: false,

  setUser: (user) => set({ user }),
  setLoading: (loading) => set({ isLoading: loading }),
  setIntroCompleted: (completed) => set({ hasCompletedIntro: completed }),

  // 초기화 - localStorage에서 세션 복원
  initialize: async () => {
    try {
      const savedUser = localStorage.getItem("auth_user");
      if (savedUser) {
        const user = JSON.parse(savedUser) as AuthUser;
        // 저장된 사용자 정보가 유효한지 확인 (DB에서 조회)
        const { data, error } = await supabase.rpc("get_user_profile", {
          p_user_id: user.id,
        });
        
        if (!error && data?.success && data?.user) {
          set({
            user: data.user,
            isLoading: false,
            initialized: true,
            hasCompletedIntro: false, // 인트로는 항상 표시 (다시 돌아왔어요에서 자동 인식)
          });
          return;
        }
        // 유효하지 않으면 localStorage 클리어
        localStorage.removeItem("auth_user");
      }
    } catch (e) {
      console.warn("Failed to restore session:", e);
      localStorage.removeItem("auth_user");
    }
    
    set({
      user: null,
      isLoading: false,
      initialized: true,
      hasCompletedIntro: false,
    });
  },

  // 상태 초기화
  reset: () => {
    localStorage.removeItem("auth_user");
    set({
      user: null,
      isLoading: false,
      initialized: true,
      hasCompletedIntro: false,
    });
  },

  signIn: async (username: string, password: string) => {
    try {
      set({ isLoading: true });

      // SQL 함수 호출
      const { data, error } = await supabase.rpc("signin", {
        p_username: username,
        p_password: password,
      });

      if (error) {
        set({ isLoading: false });
        return { error: new Error(error.message) };
      }

      const result = data as { success: boolean; user?: AuthUser; error?: string };

      if (!result.success || !result.user) {
        set({ isLoading: false });
        return { error: new Error(result.error || "로그인에 실패했습니다.") };
      }

      // localStorage에 저장 (게스트 사용자만)
      if (!result.user.username.startsWith("guest_")) {
        localStorage.setItem("auth_user", JSON.stringify(result.user));
      }

      set({
        user: result.user,
        isLoading: false,
        hasCompletedIntro: true,
      });

      return { error: null };
    } catch (error) {
      set({ isLoading: false });
      return { error: error as Error };
    }
  },

  signUp: async (username: string, password: string) => {
    try {
      set({ isLoading: true });

      // SQL 함수 호출
      const { data, error } = await supabase.rpc("signup", {
        p_username: username,
        p_password: password,
      });

      if (error) {
        set({ isLoading: false });
        return { error: new Error(error.message) };
      }

      const result = data as { success: boolean; user?: AuthUser; error?: string };

      if (!result.success || !result.user) {
        set({ isLoading: false });
        return { error: new Error(result.error || "회원가입에 실패했습니다.") };
      }

      // localStorage에 저장
      localStorage.setItem("auth_user", JSON.stringify(result.user));

      set({
        user: result.user,
        isLoading: false,
        hasCompletedIntro: true,
      });

      return { error: null };
    } catch (error) {
      set({ isLoading: false });
      return { error: error as Error };
    }
  },

  getUsername: () => {
    const state = useAuthStore.getState();
    return state.user?.username || null;
  },

  getUserId: () => {
    const state = useAuthStore.getState();
    return state.user?.id || null;
  },

  anonymousSignIn: async () => {
    try {
      set({ isLoading: true });

      // SQL 함수 호출
      const { data, error } = await supabase.rpc("anonymous_signin");

      if (error) {
        set({ isLoading: false });
        return { error: new Error(error.message) };
      }

      const result = data as { success: boolean; user?: AuthUser; error?: string };

      if (!result.success || !result.user) {
        set({ isLoading: false });
        return { error: new Error(result.error || "익명 로그인에 실패했습니다.") };
      }

      set({
        user: result.user,
        isLoading: false,
        hasCompletedIntro: true,
      });

      return { error: null };
    } catch (error) {
      set({ isLoading: false });
      return { error: error as Error };
    }
  },

  isAnonymous: () => {
    const state = useAuthStore.getState();
    if (!state.user) return true; // 로그인 안되면 익명 취급
    // guest_로 시작하면 익명 사용자
    return state.user.username.startsWith("guest_");
  },

  signOut: async () => {
    localStorage.removeItem("auth_user");
    set({
      user: null,
      isLoading: false,
      hasCompletedIntro: false,
    });
  },

  updateUserColor: async (color: string) => {
    try {
      const state = useAuthStore.getState();
      if (!state.user) {
        return { error: new Error("로그인이 필요합니다.") };
      }

      // SQL 함수 호출
      const { data, error } = await supabase.rpc("update_user_color", {
        p_user_id: state.user.id,
        p_new_color: color,
      });

      if (error) {
        return { error: new Error(error.message) };
      }

      const result = data as { success: boolean; user?: AuthUser; error?: string };

      if (!result.success) {
        return { error: new Error(result.error || "컬러 변경에 실패했습니다.") };
      }

      // 로컬 상태 업데이트
      set({
        user: { ...state.user, user_color: color },
      });

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },
}));
